import store from '@/store';
import routerInstance from '@/router/index';
import StoreDetail from '@/views/store/index.vue';
import { isLocalChatUiFixtureEnabled } from '@/views/chat/__fixtures__/chatUiFixture';
import {
  shouldClearSearchProductOrigin,
  shouldKeepSearchProductCache
} from '@/router/searchProductFlow';

const restrictedPattern = [
  /\/my\/(info|password|withdraw|point|coupon|review|order\/history|fav\/?.*|address|recent\/?.*|service\/?.*|settings)/g,
  /\/chat\/?.*/g,
  /\/order\/?.*/g,
  /\/fav\/?.*/g
];
const checkPattern = (path) => {
  return restrictedPattern.filter((pattern) => pattern.test(path)).length > 0;
};
const isLocalChatUiFixtureRoute = (path) =>
  /^\/chat\//.test(path) || /^\/room\/\d+\/(ORDER|INQUIRY)$/.test(path);

export const isPassedTokenCheck = async (to, from, next) => {
  const token = store.getters['user/getAccessToken'];
  const { path } = to;
  if (isLocalChatUiFixtureEnabled() && isLocalChatUiFixtureRoute(path)) return true;
  if (!token && checkPattern(path)) return false;
  else return true;
};

export const shouldForceRelogin = async (to) => {
  const isForced = await store.dispatch('user/ENFORCE_RELOGIN_POLICY');
  if (!isForced) return false;

  if (to.path === '/sign/in') return '/sign/in';
  if (isInsertToGo(to.path)) return `/sign/in?togo=${to.path}`;
  return '/sign/in';
};

export const requiresActiveSessionCheck = (path) => checkPattern(path);

const toGoPattern = [/\/chat\/?.*/g, /\/order\/?.*/g, /\/fav\/?.*/g];
export const isInsertToGo = (path) => {
  return toGoPattern.filter((pattern) => pattern.test(path)).length > 0;
};

export const notRegisteredRoutePath = (to, from, next) => {
  return !routerInstance.hasRoute(to.name);
};

const emitterRemovePattern = [/(\/my)(\/.*)?/g, /(\/fav)(\/.*)?/g, /(\/main)(\/.*)?/g, /^(\/order)(\/.*)?/g];
const checkEmitterPattern = (path) => {
  return (
    emitterRemovePattern.filter((pattern) => {
      return pattern.test(path);
    }).length > 0
  );
};
export const disconnectSocket = (to, from, next) => {
  const { path } = to;

  if (checkEmitterPattern(path)) {
    console.warn('============ SOCKET DISCONNECT ============');
    const { userNo } = store.getters['user/getUserData'];
    store.dispatch('sse/emitterDisconnect');
    store.dispatch('chat/disconnect');
  }
};

export const heartBeat = async () => {
  await store.dispatch('user/IS_ACTIVE');
  const { status } = store.getters['user/getUserData'];
  const result = status === 'ACTIVE';
  if (!result) {
    await store.dispatch('user/SIGN_OUT');
    return;
  }

  return result;
};

const isOrderHistoryListPath = (path) => /^\/(?:my\/)?order\/history$/i.test(path);
const isOrderHistoryDetailPath = (path) => /^\/my\/order\/history\/\d+$/i.test(path);
const isOrderHistoryProductPath = (path) => /^\/my\/order\/history\/product\/[^/]+\/[^/]+$/i.test(path);
const isOrderHistoryStorePath = (path) =>
  /^\/shop\/[^/]+\/(?:product(?:\/[^/]+)?|category|search|information)$/i.test(path);
const isOrderHistoryProductFlowPath = (path) =>
  isOrderHistoryProductPath(path) || /^\/cart$/i.test(path) || isOrderHistoryStorePath(path);
const isOrderHistoryPath = (path) =>
  isOrderHistoryListPath(path) || isOrderHistoryDetailPath(path) || isOrderHistoryProductPath(path);
const shouldKeepOrderHistory = (toPath, fromPath) => {
  const isOrderHistoryRoundTrip = isOrderHistoryPath(toPath) && isOrderHistoryPath(fromPath);
  const fromStorePath = store.getters['layout/getFromStorePath'];
  const hasOrderHistoryCache = (store.getters['layout/keepAliveList'] ?? []).includes('ORDER_HISTORY');
  const isOrderProductStoreFlow =
    hasOrderHistoryCache &&
    isOrderHistoryProductPath(fromStorePath) &&
    isOrderHistoryProductFlowPath(toPath) &&
    isOrderHistoryProductFlowPath(fromPath);
  const isOrderProductCartRoundTrip =
    hasOrderHistoryCache &&
    ((isOrderHistoryProductPath(fromPath) && /^\/cart$/i.test(toPath)) ||
      (/^\/cart$/i.test(fromPath) && isOrderHistoryProductPath(toPath)));

  return isOrderHistoryRoundTrip || isOrderProductStoreFlow || isOrderProductCartRoundTrip;
};
const mergeKeepAlive = (componentNames, ...names) => [...new Set([...componentNames, ...names])];

export const keepAlive = (to, from) => {
  const toPath = to.path;
  const fromPath = from.path;
  const storeProductPages = ['STORE', 'STORE_DETAIL', 'SEARCH'];
  const storeProductDetailPages = [...storeProductPages, 'SEARCH_RESULT'];
  const cartReturnPath = toPath === '/cart' ? fromPath : fromPath === '/cart' ? toPath : '';

  let componentName = [];

  console.log('toPath {}', toPath);

  if (/\/search$/gi.test(toPath)) componentName = ['SEARCH', 'STORE'];
  // if (/\/search/gi.test(toPath)) componentName = ['SEARCH_RESULT'];
  if (/^\/shop\/[\d]+\/product\/[\d]+$/gi.test(toPath)) componentName = storeProductDetailPages;
  if (
    (/^\/shop\/[\d]+\/(product|search)$/gi.test(fromPath) && /^\/cart$/gi.test(toPath)) ||
    (/^\/shop\/[\d]+\/product\/[\d]+$/gi.test(fromPath) && /^\/cart$/gi.test(toPath)) ||
    (/^\/cart$/gi.test(fromPath) && /^\/shop\/[\d]+\/(product|search)$/gi.test(toPath))
  )
    componentName = storeProductPages;
  if (/^\/shop\/[\d]+\/(product|search)$/gi.test(toPath)) componentName = storeProductPages;
  if (
    (/^\/search\/result(\/[\d]+)?$/gi.test(fromPath) && /^\/shop\/[\d]+\/product$/gi.test(toPath)) ||
    (/^\/search\/result(\/[\d]+)?$/gi.test(toPath) && /^\/shop\/[\d]+\/product$/gi.test(fromPath))
  )
    componentName = storeProductDetailPages;
  if (/^\/shop\/[\d]+\/(information|comment|category)$/gi.test(toPath)) componentName = ['STORE'];
  if (
    (/\/main/gi.test(fromPath) && /\/my\/(event|notice)\/[\d]+/gi.test(toPath)) ||
    (/\/main/gi.test(toPath) && /\/my\/(event|notice)\/[\d]+/gi.test(fromPath))
  )
    componentName = ['MAIN'];
  if (
    (/\/chat\/order/gi.test(fromPath) && /\/room\/[\d]+\/ORDER/gi.test(toPath)) ||
    (/\/chat\/order/gi.test(toPath) && /\/room\/[\d]+\/ORDER/gi.test(fromPath))
  )
    componentName = ['CHAT_LOBBY', 'LOBBY_ORDER'];

  if (
    (/\/chat\/inquiry/gi.test(fromPath) && /\/room\/[\d]+\/INQUIRY/gi.test(toPath)) ||
    (/\/chat\/inquiry/gi.test(toPath) && /\/room\/[\d]+\/INQUIRY/gi.test(fromPath))
  )
    componentName = ['CHAT_LOBBY', 'LOBBY_INQUIRY'];

  if (
    (/\/chat\/all/gi.test(fromPath) && /\/room\/[\d]+\/(ORDER|INQUIRY)/gi.test(toPath)) ||
    (/\/chat\/all/gi.test(toPath) && /\/room\/[\d]+\/(ORDER|INQUIRY)/gi.test(fromPath))
  )
    componentName = ['CHAT_LOBBY', 'LOBBY_ALL'];

  if (
    (/\/fav\/shop$/gi.test(fromPath) && /\/shop\/*/gi.test(toPath)) ||
    (/\/fav\/shop$/gi.test(toPath) && /\/shop\/*/gi.test(fromPath))
  )
    componentName = ['FAV', 'FAV_SHOP'];

  if (
    (/\/fav\/product/gi.test(fromPath) && /\/shop\/*/gi.test(toPath)) ||
    (/\/fav\/product/gi.test(toPath) && /\/shop\/*/gi.test(fromPath))
  )
    componentName = ['FAV', 'FAV_PRODUCT'];

  if (
    (/\/shop\/admin\/[\d]+\/product/gi.test(toPath) &&
      /\/shop\/admin\/[\d]+\/product\/[\d]+\/basic/gi.test(fromPath)) ||
    (/\/shop\/admin\/[\d]+\/product/gi.test(fromPath) && /\/shop\/admin\/[\d]+\/product\/[\d]+\/basic/gi.test(toPath))
  )
    componentName = ['PRODUCT_MANAGE'];

  // REVIEW_MANAGE
  // /shop/21/review
  // /shop/21/repl/90
  // /report/${item.reviewNo}`
  // /shop/manage/customer/${item.storeNo}/${item.userNo}

  if (
    (/\/shop\/[\d]+\/review/gi.test(fromPath) &&
      (/\/shop\/[\d]+\/repl\/[\d]+/gi.test(toPath) ||
        /\/report\/[\d]+/gi.test(toPath) ||
        /\/shop\/manage\/customer\/[\d]+\/[\d]+/gi.test(toPath))) ||
    (/\/shop\/[\d]+\/review/gi.test(toPath) &&
      (/\/shop\/[\d]+\/repl\/[\d]+/gi.test(fromPath) ||
        /\/report\/[\d]+/gi.test(fromPath) ||
        /\/shop\/manage\/customer\/[\d]+\/[\d]+/gi.test(fromPath)))
  )
    componentName = ['REVIEW_MANAGE'];

  // /shop/manage/order
  // /shop/manage/order/710

  // console.log(/^\/shop\/manage\/order$/.test(toPath));
  // console.log(/^\/shop\/manage\/order\/[\d]+$/.test(toPath));
  if (
    (/^\/shop\/manage\/order$/.test(fromPath) && /^\/shop\/manage\/order\/[\d]+$/.test(toPath)) ||
    (/^\/shop\/manage\/order$/.test(toPath) && /^\/shop\/manage\/order\/[\d]+$/.test(fromPath))
  )
    componentName = ['ORDER_MANAGE'];

  // /shop/47/product
  // /new
  if (
    (/\/shop\/[\d]+\/product/gi.test(toPath) && /\/new/gi.test(fromPath)) ||
    (/\/shop\/[\d]+\/product/gi.test(fromPath) && /\/new/gi.test(toPath))
  )
    componentName = ['NEW_STORE'];

  if (
    (/\/shop\/[\d]+\/product/gi.test(toPath) && /\/promotion/gi.test(fromPath)) ||
    (/\/shop\/[\d]+\/product/gi.test(fromPath) && /\/promotion/gi.test(toPath))
  )
    componentName = ['PROMOTION_STORE'];
  ///shop/22/product
  // /promotion

  if (/^\/main$/gi.test(cartReturnPath)) componentName = ['MAIN'];
  if (/^\/search\/result(\/[\d]+)?$/gi.test(cartReturnPath)) componentName = ['SEARCH_RESULT'];
  if (/^\/new$/gi.test(cartReturnPath)) componentName = ['NEW_STORE'];
  if (/^\/shop\/[\d]+(\/(information|comment|category))?$/gi.test(cartReturnPath)) componentName = ['STORE'];
  if (/^\/shop\/[\d]+\/information$/gi.test(cartReturnPath)) componentName = ['STORE', 'STORE_INFORMATION'];

  if (shouldKeepOrderHistory(toPath, fromPath)) {
    componentName = mergeKeepAlive(componentName, 'ORDER_HISTORY');
  }

  componentName = componentName.filter((name) => name !== 'SEARCH');

  if (shouldKeepSearchProductCache(toPath, fromPath)) {
    componentName = mergeKeepAlive(componentName, 'SEARCH');
  }

  const fromStorePath = store.getters['layout/getFromStorePath'];
  if (shouldClearSearchProductOrigin(toPath, fromStorePath)) {
    store.commit('layout/setFromStorePath', '');
  }

  console.log('componentName {}', componentName);
  store.commit('layout/keepAlive', componentName);
};
